import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaSignInAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSettings } from "../hooks/useSettings";
import LanguageSwitcher from "./LanguageSwitcher";
import AccessibleButton from "./ui/AccessibleButton";
import { announceToScreenReader } from "../utils/focusManagement";
import defaultLogo from "../assets/logo.jpg";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("home");
  const { theme } = useSettings();
  const { t } = useTranslation();
  const location = useLocation();

  const toggleMenu = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    announceToScreenReader(newState ? 'Menu opened' : 'Menu closed');
  };

  // Update active link based on current route
  useEffect(() => {
    const path = location.pathname;
    
    if (path === "/") {
      setActiveLink("home");
    } else if (path === "/courses") {
      setActiveLink("courses");
    } else if (path === "/events") {
      setActiveLink("events");
    } else if (path.startsWith("/news")) {
      setActiveLink("news");
    } else if (path === "/top-students") {
      setActiveLink("topStudents");
    } else if (path === "/about") {
      setActiveLink("about");
    } else if (path === "/donate") {
      setActiveLink("donate");
    } else if (path === "/contact") {
      setActiveLink("contact");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Only update active section based on scroll if on home page
      if (location.pathname === "/") {
        const sections = [
          "home",
          "courses",
          "events",
          "topstudents",
          "about",
          "donate",
          "contact",
        ];
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) {
              setActiveLink(section);
              break;
            }
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const navLinks = [
    { href: "/", text: t("navbar.home"), id: "home" },
    { href: "/courses", text: t("navbar.courses"), id: "courses" },
    { href: "/scholarships", text: t("navbar.events"), id: "scholarships" },
    { href: "/news", text: t("navbar.news"), id: "news" },
    { href: "/top-students", text: t("navbar.topStudents"), id: "topStudents" },
    { href: "/about", text: t("navbar.about"), id: "about" },
    { href: "/donate", text: t("navbar.donate"), id: "donate" },
    { href: "/contact", text: t("navbar.contact"), id: "contact" },
  ];

  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-safir-dark shadow-md py-1" : "bg-transparent py-2"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <Link to="/" className="flex items-center">
              <img 
                src={theme?.logo || defaultLogo} 
                alt="Safir the Elite" 
                className="h-10 mr-2 ml-2 object-contain" 
                onError={(e) => {
                  e.target.src = defaultLogo;
                }}
              />
              <span className="text-white font-semibold text-xl">
                Safir the Elite
              </span>
            </Link>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex gap-4 lg:gap-8 xl:gap-10">
            {navLinks.map((link) =>
              link.href.startsWith("/") && !link.href.includes("#") ? (
                <motion.div
                  key={link.id}
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Link
                    to={link.href}
                    className={`text-white transition relative whitespace-nowrap px-2 py-1 text-sm lg:text-base ${
                      activeLink === link.id
                        ? "text-safir-primary"
                        : "hover:text-safir-primary"
                    }`}
                  >
                    {link.text}
                    {activeLink === link.id && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-safir-primary"
                        layoutId="activeSection"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        }}
                      />
                    )}
                  </Link>
                </motion.div>
              ) : (
                <motion.a
                  key={link.id}
                  href={link.href}
                  className={`text-white transition relative whitespace-nowrap px-2 py-1 text-sm lg:text-base ${
                    activeLink === link.id
                      ? "text-safir-primary"
                      : "hover:text-safir-primary"
                  }`}
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  {link.text}
                  {activeLink === link.id && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-safir-primary"
                      layoutId="activeSection"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 20,
                      }}
                    />
                  )}
                </motion.a>
              )
            )}
          </div>

          {/* Language Switcher and Login */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            <LanguageSwitcher />
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Link
                to="/login"
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  scrolled
                    ? "bg-safir-primary hover:bg-safir-secondary text-white"
                    : "bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-sm"
                }`}
              >
                <FaSignInAlt size={12} />
                <span>{t('navbar.login')}</span>
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-white p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
            onClick={toggleMenu}
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.2)" }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="md:hidden mt-4 pb-4"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-4">
                <div className="flex flex-col space-y-3">
                  {navLinks.map((link) =>
                    link.href.startsWith("/") && !link.href.includes("#") ? (
                      <motion.div
                        key={link.id}
                        whileHover={{ x: 5 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <Link
                          to={link.href}
                          className={`text-white transition py-2 px-3 rounded-md hover:bg-white/10 ${
                            activeLink === link.id ? "text-safir-primary bg-white/5" : ""
                          }`}
                          onClick={toggleMenu}
                        >
                          {link.text}
                        </Link>
                      </motion.div>
                    ) : (
                      <motion.a
                        key={link.id}
                        href={link.href}
                        className={`text-white transition py-2 px-3 rounded-md hover:bg-white/10 ${
                          activeLink === link.id ? "text-safir-primary bg-white/5" : ""
                        }`}
                        onClick={toggleMenu}
                        whileHover={{ x: 5, color: "#1F7D53" }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        {link.text}
                      </motion.a>
                    )
                  )}
                  <div className="border-t border-white/20 pt-3">
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <LanguageSwitcher />
                      </div>
                    </motion.div>
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <Link
                        to="/login"
                        className="flex items-center gap-2 text-safir-primary font-medium py-2 px-3 rounded-md hover:bg-white/10"
                        onClick={toggleMenu}
                      >
                        <FaSignInAlt size={16} />
                        <span>{t('navbar.login')}</span>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
